import React, { useState, useEffect, useRef } from "react";
import AdminDashboard from "../Dashboard/Dashboard";
import axios from "axios";
import { BASE_URL } from "../../Api/ApiUrls";

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

const AdminRequests = () => {
  const [consultantReqs, setConsultantReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("Interior"); // Set default to Interior
  const [search, setSearch] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const isTabSwitchingRef = useRef(false);

  const fetchRequests = async (
    page = currentPage,
    size = pageSize,
    isProgressive = false,
    chunkSize = null,
    chunkNumber = 0,
    searchQuery = search,
    filterValue = filter, // Add filter parameter
    suppressLoader = false
  ) => {
    if (!isProgressive && !suppressLoader) {
      setLoading(true);
    } else if (isProgressive && !suppressLoader) {
      setProgressiveLoading(true);
    }

    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      // Build URL with filter parameter
      let url = `${BASE_URL}/consultant-req/?page=${page}&page_size=${size}`;
      
      // Add filter to URL if it has a value
      if (filterValue) {
        url += `&interested_on=${encodeURIComponent(filterValue)}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      if (chunkSize && size > 100) {
        url += `&chunk=${chunkSize}&chunk_number=${chunkNumber}`;
      }

      const res = await axios.get(url, { signal });
      const data = res.data?.data || [];
      const paginationData = res.data?.pagination || {};

      if (paginationData.current_page) {
        setCurrentPage(paginationData.current_page);
        setTotalPages(paginationData.total_pages || 1);
        setTotalCount(paginationData.total_count || 0);
      }

      if (isProgressive && size > 100) {
        const recalculatedTotalPages = Math.ceil((paginationData.total_count || 0) / size);
        setTotalPages(recalculatedTotalPages);
      }

      if (isProgressive) {
        setConsultantReqs(prev => [...prev, ...data]);
      } else {
        setConsultantReqs(data);
      }

      return {
        data: data,
        pagination: paginationData
      };
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error("Error fetching requests:", err);
        setError("Failed to fetch data. Please check backend server.");
      }
    } finally {
      if (!isProgressive && !suppressLoader) {
        setLoading(false);
      } else if (isProgressive && !suppressLoader) {
        setProgressiveLoading(false);
      }
    }
  };

  // Filter change effect
  useEffect(() => {
    isTabSwitchingRef.current = true;
    setCurrentPage(1);
    setConsultantReqs([]);
    setProgressiveLoading(false);
    // Pass the current filter value when fetching
    fetchRequests(1, pageSize, false, null, 0, search, filter).finally(() => {
      isTabSwitchingRef.current = false;
    });
  }, [filter]);

  // Page size change effect
  useEffect(() => {
    setCurrentPage(1);
    setConsultantReqs([]);
    if (pageSize <= 100) {
      fetchRequests(1, pageSize, false, null, 0, search, filter);
    }
  }, [pageSize]);

  // Search debounce effect
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      if (pageSize <= 100) {
        fetchRequests(currentPage, pageSize, false, null, 0, search, filter, true);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  // Progressive loading effect
  useEffect(() => {
    if (isTabSwitchingRef.current) return;

    if (pageSize > 100 && !progressiveLoading) {
      const loadProgressively = async () => {
        const totalChunks = Math.ceil(pageSize / 100);
        let allReqs = [];

        const firstChunk = await fetchRequests(currentPage, 100, false, 100, 0, search, filter);
        if (firstChunk && firstChunk.data) {
          allReqs = [...firstChunk.data];
          setConsultantReqs([...allReqs]);
        }

        setProgressiveLoading(true);
        let batchCounter = 0;
        const batchSize = 3;

        for (let i = 1; i < totalChunks; i++) {
          const chunkResponse = await fetchRequests(currentPage, 100, true, 100, i, search, filter);
          if (chunkResponse && chunkResponse.data) {
            allReqs = [...allReqs, ...chunkResponse.data];
            batchCounter++;

            if (batchCounter >= batchSize || i === totalChunks - 1) {
              setConsultantReqs([...allReqs]);
              batchCounter = 0;
            }
          }
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        setProgressiveLoading(false);
      };
      loadProgressively();
    }
  }, [currentPage, pageSize, filter, search]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setConsultantReqs([]);
    if (pageSize <= 100) {
      fetchRequests(value, pageSize, false, null, 0, search, filter);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    setConsultantReqs([]);
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading && !progressiveLoading) {
    return (
      <>
        <AdminDashboard />
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" flexDirection="column">
          <CircularProgress />
          <Typography mt={2}>Loading Requests...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminDashboard />
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => fetchRequests(currentPage, pageSize, false, null, 0, search, filter)}>
            Retry
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <AdminDashboard />
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 1,
          borderRadius: 2,
          boxShadow: 3,
          marginTop: 10,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Request Table
        </Typography>
        <Box p={0.2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Search Requests"
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or mobile..."
                size="small"
                sx={{ width: 300 }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Records</InputLabel>
                <Select
                  value={pageSize}
                  label="Records"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={40}>40</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              {progressiveLoading && <CircularProgress size={20} />}
            </Box>

            <Box>
              <Button
                variant={filter === "Interior" ? "contained" : "outlined"}
                color="primary"
                onClick={() => setFilter("Interior")}
                sx={{ mr: 1 }}
              >
                Interior
              </Button>
              <Button
                variant={filter === "Construction" ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setFilter("Construction")}
              >
                Construction
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>S No</TableCell>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>Mobile</TableCell>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>Interest</TableCell>
                  <TableCell sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consultantReqs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No {filter} requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  consultantReqs.map((req, index) => {
                    return (
                      <TableRow key={req.request_id}>
                        <TableCell>{(currentPage - 1) * pageSize + index + 1}</TableCell>
                        <TableCell>{req.user_first_name} {req.user_last_name}</TableCell>
                        <TableCell>{req.user_mobile_no}</TableCell>
                        <TableCell>{req.user_email}</TableCell>
                        <TableCell>
                          {req.interested_on ? (
                            <Chip
                              label={req.interested_on}
                              color={req.interested_on === "Interior" ? "primary" : "secondary"}
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(req.created_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {consultantReqs.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AdminRequests;